import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
    ArrowUpCircle,
    Building2,
    BookOpen,
    Cable,
    ChevronDown,
    CreditCard,
    LayoutDashboard,
    LogOut,
    Megaphone,
    Menu,
    Package,
    Search,
    Sparkles,
    UserRound,
    X,
} from "lucide-react";
import { logout } from "../store/authSlice";
import { ROUTES } from "../config/constants";
import PageContainer from "./layout/PageContainer";
import NotificationBell from "./notifications/NotificationBell";
import {
    getTierLabel,
    getTierName,
    SUBSCRIPTION_TIERS,
} from "../utils/subscription";

const getNavLinkClass = ({ isActive }) =>
    `relative rounded-full px-3 py-2 text-sm font-medium transition-all duration-200 ease-out ${
        isActive
            ? "border border-indigo-100 bg-indigo-100 text-indigo-600"
            : "border border-transparent text-slate-600 hover:-translate-y-0.5 hover:bg-indigo-100 hover:text-slate-900"
    }`;

const getGuideNavLinkClass = ({ isActive }) =>
    `relative rounded-full border px-3 py-2 text-sm font-semibold transition-all duration-200 ease-out ${
        isActive
            ? "border-blue-300 bg-blue-100 text-blue-800"
            : "border-blue-200 bg-blue-50 text-blue-700 hover:-translate-y-0.5 hover:border-blue-300 hover:bg-blue-100 hover:text-blue-800"
    }`;

const getMobileNavLinkClass = ({ isActive }) =>
    `group flex items-center gap-3 rounded-2xl px-3 py-2.5 text-sm font-semibold transition-all duration-200 ease-out ${
        isActive
            ? "bg-indigo-50 text-indigo-700 ring-1 ring-indigo-100"
            : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
    }`;

const getMobileGuideNavLinkClass = ({ isActive }) =>
    `group flex items-center gap-3 rounded-2xl border px-3 py-2.5 text-sm font-semibold transition-all duration-200 ease-out ${
        isActive
            ? "border-blue-300 bg-blue-100 text-blue-800"
            : "border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:text-blue-800"
    }`;

const getPublicLoginLinkClass = ({ isActive }) =>
    `rounded-full border px-4 py-2 text-sm font-semibold shadow-sm transition-all duration-200 ease-out ${
        isActive
            ? "border-indigo-600 bg-indigo-600 text-white"
            : "border-slate-200 bg-white text-slate-700 hover:-translate-y-0.5 hover:bg-slate-50 hover:text-slate-900"
    }`;

const getPublicSignupLinkClass = ({ isActive }) =>
    `rounded-full border px-4 py-2 text-sm font-semibold shadow-sm transition-all duration-200 ease-out ${
        isActive
            ? "border-indigo-600 bg-indigo-600 text-white"
            : "border-slate-200 bg-white text-slate-700 hover:-translate-y-0.5 hover:bg-slate-50 hover:text-slate-900"
    }`;

const getMobilePublicLoginLinkClass = ({ isActive }) =>
    `rounded-xl border px-3 py-2 text-xs font-semibold shadow-sm transition-colors ${
        isActive
            ? "border-indigo-600 bg-indigo-600 text-white"
            : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
    }`;

const getMobilePublicSignupLinkClass = ({ isActive }) =>
    `rounded-xl border px-3 py-2 text-xs font-semibold shadow-sm transition-colors ${
        isActive
            ? "border-indigo-600 bg-indigo-600 text-white"
            : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
    }`;

const getPlanBadgeClass = (tierName) => {
    if (tierName === SUBSCRIPTION_TIERS.ENTERPRISE) {
        return "bg-amber-50 text-amber-700 ring-1 ring-amber-100";
    }

    if (tierName === SUBSCRIPTION_TIERS.PRO) {
        return "bg-indigo-50 text-indigo-600 ring-1 ring-indigo-100";
    }

    return "bg-slate-50 text-slate-600 ring-1 ring-slate-200";
};

const Navbar = () => {
    const { isAuthenticated, user, credits } = useSelector(
        (state) => state.auth,
    );
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
    const accountMenuRef = useRef(null);
    const mobileAccountMenuRef = useRef(null);
    const tierName = getTierName(credits, user?.plan);
    const tierLabel = getTierLabel(tierName);
    const planBadgeClass = getPlanBadgeClass(tierName);
    const accountDisplayName = user?.full_name || user?.email;
    const subscriptionActionLabel =
        tierName === SUBSCRIPTION_TIERS.ENTERPRISE
            ? "Change Plan"
            : "Upgrade Plan";

    const canViewMainNav = isAuthenticated && user?.is_active;
    const primaryLinks = canViewMainNav
        ? [
              {
                  label: "Dashboard",
                  to: ROUTES.DASHBOARD,
                  icon: LayoutDashboard,
              },
              { label: "Search", to: ROUTES.SEARCH, icon: Search },
              { label: "Product", to: ROUTES.PRODUCTS, icon: Package },
              { label: "Campaign", to: ROUTES.CAMPAIGNS, icon: Megaphone },
              {
                  label: "Guides",
                  to: ROUTES.GUIDES,
                  icon: BookOpen,
                  variant: "guide",
              },
          ]
        : [];
    const accountLinks = [
        ...(isAuthenticated
            ? [{ label: "My Profile", to: ROUTES.PROFILE, icon: UserRound }]
            : []),
    ];
    const accountMenuLinks = [
        { label: "My Profile", to: ROUTES.PROFILE, icon: UserRound },
        { label: "BYO Connections", to: ROUTES.CONNECTIONS, icon: Cable },
        {
            label: "Organization Context",
            to: ROUTES.ORGANIZATION_CONTEXT,
            icon: Building2,
        },
        {
            label: "Subscription",
            to: ROUTES.MANAGE_SUBSCRIPTION,
            icon: CreditCard,
        },
        {
            label: subscriptionActionLabel,
            to: ROUTES.MANAGE_SUBSCRIPTION,
            icon: ArrowUpCircle,
            variant: "upgrade",
            state: { fromProfileDropdown: true },
        },
    ];

    useEffect(() => {
        setIsMobileMenuOpen(false);
        setIsAccountMenuOpen(false);
    }, [location.pathname, location.search]);

    useEffect(() => {
        if (!isAccountMenuOpen) return;

        const handlePointerDown = (event) => {
            if (accountMenuRef.current?.contains(event.target)) return;
            if (mobileAccountMenuRef.current?.contains(event.target)) return;

            setIsAccountMenuOpen(false);
        };

        window.addEventListener("pointerdown", handlePointerDown);

        return () => {
            window.removeEventListener("pointerdown", handlePointerDown);
        };
    }, [isAccountMenuOpen]);

    const handleLogout = () => {
        setIsMobileMenuOpen(false);
        setIsAccountMenuOpen(false);
        dispatch(logout());
        navigate(ROUTES.LOGIN);
    };

    const renderDesktopLink = (link) => (
        <NavLink
            key={link.to}
            to={link.to}
            className={
                link.variant === "guide" ? getGuideNavLinkClass : getNavLinkClass
            }
        >
            {link.label}
        </NavLink>
    );

    const renderMobileLink = (link) => {
        const Icon = link.icon;

        return (
            <NavLink
                key={link.to}
                to={link.to}
                onClick={() => setIsMobileMenuOpen(false)}
                className={
                    link.variant === "guide"
                        ? getMobileGuideNavLinkClass
                        : getMobileNavLinkClass
                }
            >
                {({ isActive }) => (
                    <>
                        {Icon && (
                            <span
                                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-all duration-200 ${
                                    link.variant === "guide" && isActive
                                        ? "bg-white text-blue-700 ring-1 ring-blue-200"
                                        : link.variant === "guide"
                                          ? "bg-white/80 text-blue-600 ring-1 ring-blue-100 group-hover:bg-white group-hover:text-blue-700"
                                          : isActive
                                        ? "bg-white text-indigo-700 ring-1 ring-indigo-100"
                                        : "bg-slate-100 text-slate-500 group-hover:bg-indigo-50 group-hover:text-indigo-600"
                                }`}
                            >
                                <Icon size={17} aria-hidden="true" />
                            </span>
                        )}
                        <span className="min-w-0 truncate">{link.label}</span>
                    </>
                )}
            </NavLink>
        );
    };

    const renderAccountMenu = (className) => (
        <div
            role="menu"
            className={`app-dropdown-enter overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-900/10 ${className}`}
        >
            <div className="border-b border-slate-100 bg-indigo-50/60 px-4 py-3">
                <p className="truncate text-sm font-semibold text-slate-900">
                    {accountDisplayName}
                </p>
                <p className="mt-1 text-xs font-medium text-slate-500">
                    {tierLabel} plan
                </p>
            </div>

            <div className="p-2">
                {accountMenuLinks.map((link) => {
                    const Icon = link.icon;
                    const isUpgradeLink =
                        link.state?.fromProfileDropdown === true;
                    const isUpgradeView =
                        location.state?.fromProfileDropdown === true;
                    const isUpgradeAction = link.variant === "upgrade";
                    const isLinkActive = (isActive) =>
                        isActive && isUpgradeLink === isUpgradeView;

                    return (
                        <NavLink
                            key={`${link.to}-${link.label}`}
                            to={link.to}
                            state={link.state}
                            role="menuitem"
                            onClick={() => {
                                setIsAccountMenuOpen(false);
                                setIsMobileMenuOpen(false);
                            }}
                            className={({ isActive }) => {
                                const isActiveLink = isLinkActive(isActive);

                                return `group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition ${
                                    isActiveLink
                                        ? isUpgradeAction
                                            ? "bg-violet-100 text-violet-800 ring-1 ring-violet-300"
                                            : "bg-indigo-50 text-indigo-700"
                                        : isUpgradeAction
                                            ? "bg-violet-50 text-violet-700 ring-1 ring-violet-200 hover:bg-violet-100"
                                            : "text-slate-600 hover:bg-indigo-50 hover:text-indigo-700"
                                }`;
                            }}
                        >
                            {({ isActive }) => {
                                const isActiveLink = isLinkActive(isActive);

                                return (
                                    <>
                                        <span
                                            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                                                isActiveLink
                                                    ? isUpgradeAction
                                                        ? "bg-white text-violet-800 ring-1 ring-violet-300"
                                                        : "bg-white text-indigo-700 ring-1 ring-indigo-100"
                                                    : isUpgradeAction
                                                        ? "bg-violet-100 text-violet-700 ring-1 ring-violet-300"
                                                        : "bg-slate-100 text-slate-500 transition group-hover:bg-indigo-50 group-hover:text-indigo-700 group-hover:ring-1 group-hover:ring-indigo-100"
                                            }`}
                                        >
                                            <Icon size={16} aria-hidden="true" />
                                        </span>
                                        <span>{link.label}</span>
                                    </>
                                );
                            }}
                        </NavLink>
                    );
                })}
            </div>

            <div className="border-t border-slate-100 p-2">
                <button
                    type="button"
                    onClick={handleLogout}
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold text-red-700 transition hover:bg-red-50"
                >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-red-50 text-red-600">
                        <LogOut size={16} aria-hidden="true" />
                    </span>
                    Logout
                </button>
            </div>
        </div>
    );

    return (
        <>
            <nav className="sticky top-0 z-[60] border-b border-slate-200 bg-gradient-to-r from-white via-indigo-50 to-white text-slate-900 shadow-[0_2px_10px_rgba(15,23,42,0.04)]">
                <PageContainer>
                    <div className="relative flex h-16 items-center justify-between gap-4">
                        <Link
                            to={ROUTES.LANDING_PAGE}
                            className={`group flex min-w-0 items-center gap-3 ${
                                isAuthenticated
                                    ? "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 lg:static lg:translate-x-0 lg:translate-y-0"
                                    : ""
                            }`}
                            aria-label="Go to home"
                        >
                            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-indigo-600 text-white shadow-[0_0_0_5px_rgba(238,242,255,0.85),0_8px_18px_rgba(80,77,233,0.14)] transition-transform duration-200 group-hover:scale-105">
                                <Sparkles size={20} aria-hidden="true" />
                            </span>
                            <span className="min-w-0">
                                <span className="block truncate text-base font-bold tracking-tight text-slate-950 sm:text-lg">
                                    OptiMind's Optilead
                                </span>
                                <span className="hidden truncate text-xs font-medium text-slate-500 sm:block">
                                    Autonomous AI Swarm
                                </span>
                            </span>
                        </Link>

                    <div className="hidden items-center gap-2 lg:flex">
                        {primaryLinks.map(renderDesktopLink)}
                    </div>

                    <div className="hidden items-center gap-3 lg:flex">
                        {isAuthenticated ? (
                            <>
                                <NotificationBell />

                                <div className="relative" ref={accountMenuRef}>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setIsAccountMenuOpen(
                                                (currentValue) =>
                                                    !currentValue,
                                            )
                                        }
                                        aria-haspopup="menu"
                                        aria-expanded={isAccountMenuOpen}
                                        className="flex max-w-[300px] items-center gap-2 rounded-full border border-indigo-100 bg-indigo-50 px-3 py-1.5 shadow-[0_1px_6px_rgba(15,23,42,0.035)] transition hover:border-indigo-200 hover:bg-indigo-100 hover:shadow-sm"
                                    >
                                        <span className="truncate text-sm font-medium text-slate-700">
                                            {accountDisplayName}
                                        </span>
                                        <span
                                            className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${planBadgeClass}`}
                                        >
                                            {tierLabel}
                                        </span>
                                        <ChevronDown
                                            size={15}
                                            className={`shrink-0 text-slate-400 transition-transform ${
                                                isAccountMenuOpen
                                                    ? "rotate-180"
                                                    : ""
                                            }`}
                                            aria-hidden="true"
                                        />
                                    </button>

                                    {isAccountMenuOpen &&
                                        renderAccountMenu(
                                            "absolute right-0 top-full z-[90] mt-3 w-72",
                                        )}
                                </div>
                            </>
                        ) : (
                            <div className="flex items-center gap-2">
                                <NavLink
                                    to={ROUTES.LOGIN}
                                    className={getPublicLoginLinkClass}
                                >
                                    Login
                                </NavLink>
                                <NavLink
                                    to={ROUTES.SIGNUP}
                                    className={getPublicSignupLinkClass}
                                >
                                    Sign Up
                                </NavLink>
                            </div>
                        )}
                    </div>

                    {!isAuthenticated && (
                        <div className="ml-auto flex shrink-0 items-center gap-2 lg:hidden">
                            <NavLink
                                to={ROUTES.LOGIN}
                                className={getMobilePublicLoginLinkClass}
                            >
                                Login
                            </NavLink>
                            <NavLink
                                to={ROUTES.SIGNUP}
                                className={getMobilePublicSignupLinkClass}
                            >
                                Sign Up
                            </NavLink>
                        </div>
                    )}

                    {isAuthenticated && (
                        <button
                            type="button"
                            onClick={() =>
                                setIsMobileMenuOpen(
                                    (currentValue) => !currentValue,
                                )
                            }
                            className="order-first inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 shadow-sm transition-all duration-200 hover:bg-slate-50 lg:hidden"
                            aria-label={
                                isMobileMenuOpen
                                    ? "Close navigation menu"
                                    : "Open navigation menu"
                            }
                            aria-expanded={isMobileMenuOpen}
                        >
                            <Menu size={20} aria-hidden="true" />
                        </button>
                    )}

                    {isAuthenticated && (
                        <div className="ml-auto flex shrink-0 items-center gap-2 lg:hidden">
                            <NotificationBell />

                            <div
                                ref={mobileAccountMenuRef}
                                className="relative"
                            >
                                <button
                                    type="button"
                                    onClick={() =>
                                        setIsAccountMenuOpen(
                                            (currentValue) => !currentValue,
                                        )
                                    }
                                    aria-label="Open account menu"
                                    aria-haspopup="menu"
                                    aria-expanded={isAccountMenuOpen}
                                    title="Account menu"
                                    className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border shadow-sm transition-colors ${
                                        isAccountMenuOpen ||
                                        location.pathname === ROUTES.PROFILE
                                            ? "border-indigo-200 bg-indigo-50 text-indigo-700"
                                            : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                                    }`}
                                >
                                    <UserRound size={19} aria-hidden="true" />
                                </button>

                                {isAccountMenuOpen &&
                                    renderAccountMenu(
                                        "absolute right-0 top-full z-[90] mt-3 w-[min(calc(100vw-1.5rem),18rem)]",
                                    )}
                            </div>
                        </div>
                    )}
                    </div>
                </PageContainer>
            </nav>

            {isAuthenticated && (
                <>
                    <button
                        type="button"
                        aria-label="Close navigation menu"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`fixed inset-0 z-[70] bg-slate-900/40 transition-opacity duration-300 lg:hidden ${
                            isMobileMenuOpen
                                ? "pointer-events-auto opacity-100"
                                : "pointer-events-none opacity-0"
                        }`}
                    />

                    <aside
                        aria-hidden={!isMobileMenuOpen}
                        className={`fixed inset-y-0 left-0 z-[80] flex w-[80vw] max-w-[320px] flex-col overflow-hidden border-r border-slate-200 bg-white text-slate-900 shadow-2xl transition-transform duration-300 ease-out lg:hidden ${
                            isMobileMenuOpen
                                ? "translate-x-0"
                                : "-translate-x-full"
                        }`}
                    >
                        <div className="flex shrink-0 items-center justify-between border-b border-slate-200 bg-white px-4 py-4">
                            <div className="flex min-w-0 items-center gap-2.5">
                                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-600 text-white shadow-[0_10px_20px_rgba(79,70,229,0.18)]">
                                    <Sparkles size={18} aria-hidden="true" />
                                </span>
                                <div className="min-w-0">
                                    <p className="truncate text-lg font-bold text-slate-950">
                                        OptiMind
                                    </p>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setIsMobileMenuOpen(false)}
                                aria-label="Close navigation menu"
                                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 transition hover:border-indigo-100 hover:bg-indigo-50 hover:text-indigo-700"
                            >
                                <X size={19} aria-hidden="true" />
                            </button>
                        </div>

                        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
                            <div className="space-y-1.5">
                                {primaryLinks.map(renderMobileLink)}
                                {accountLinks.map(renderMobileLink)}
                            </div>
                        </div>

                        <div className="shrink-0 border-t border-slate-200 bg-slate-50 p-4">
                            <div className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5 shadow-sm">
                                <p className="truncate text-sm font-semibold text-slate-900">
                                    {accountDisplayName}
                                </p>
                                <div className="mt-2 flex flex-wrap gap-2">
                                    <span
                                        className={`rounded-full px-2 py-0.5 text-xs font-semibold ${planBadgeClass}`}
                                    >
                                        {tierLabel}
                                    </span>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={handleLogout}
                                className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm font-semibold text-red-700 shadow-sm transition-all duration-200 hover:border-red-300 hover:bg-red-100 hover:text-red-800"
                            >
                                <LogOut size={16} aria-hidden="true" />
                                Logout
                            </button>
                        </div>
                    </aside>
                </>
            )}
        </>
    );
};

export default Navbar;
